import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/**
 * The town herald, sent on whenever a rock is marked done.
 *
 * A rock is a quarter's work, and a status dropdown changing colour is a poor
 * reward for it. So a herald in red strides on, raises a bugle, plays a
 * fanfare, and a scroll unfurls proclaiming the rock complete — by name — under
 * a fall of confetti. About six seconds, then gone. A click anywhere or Escape
 * sends him off early; nobody should have to sit through it twice in a meeting.
 *
 * Everything is drawn here — inline SVG, CSS keyframes in app.css, the fanfare
 * synthesised with Web Audio — so it costs no dependency and no asset request.
 *
 * Under prefers-reduced-motion he does not stride, nothing falls and nothing
 * plays: the scroll simply fades in, is read, and fades out. That is decided in
 * JS rather than by a media query alone because the sound and the timing change
 * too, not just the CSS.
 */
export function RockHerald({ title, onDone }: { title: string; onDone: () => void }) {
  const calm = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );
  const [leaving, setLeaving] = useState(false);
  const finished = useRef(false);

  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    const fade = window.setTimeout(() => setLeaving(true), calm ? 3600 : 5800);
    const end = window.setTimeout(finish, calm ? 4000 : 6300);
    const audio = calm ? null : playFanfare();
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(end);
      /* Closing the context cuts the fanfare off mid-note if he is dismissed. */
      void audio?.close().catch(() => undefined);
    };
  }, [calm, finish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        finish();
      }
    };
    /* Capture, so a modal underneath does not also close on the same Escape. */
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [finish]);

  /* Rolled once per proclamation, not per render, or the confetti would jump
     every time the overlay re-rendered to fade out. */
  const confetti = useMemo(
    () =>
      calm
        ? []
        : Array.from({ length: 80 }, (_, i) => ({
            key: i,
            style: {
              '--x': `${Math.random() * 100}vw`,
              '--dx': `${(Math.random() - 0.5) * 30}vw`,
              '--r': `${(Math.random() - 0.5) * 1440}deg`,
              '--d': `${2.3 + Math.random() * 0.8}s`,
              '--dur': `${2 + Math.random() * 1}s`,
              '--c': CONFETTI[i % CONFETTI.length],
            } as CSSProperties,
          })),
    [calm],
  );

  return createPortal(
    <div
      className={`herald-overlay${calm ? ' herald-calm' : ''}${leaving ? ' leaving' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-label={`Rock complete: ${title}`}
      onClick={finish}
    >
      {confetti.map((c) => (
        <span key={c.key} className="herald-confetti" style={c.style} aria-hidden="true" />
      ))}

      <div className="herald-stage">
        <div className="herald-figure" aria-hidden="true">
          <HeraldSvg />
          {!calm && (
            <>
              <span className="herald-note" style={{ '--d': '1.7s' } as CSSProperties}>♪</span>
              <span className="herald-note" style={{ '--d': '2.1s' } as CSSProperties}>♫</span>
              <span className="herald-note" style={{ '--d': '2.5s' } as CSSProperties}>♪</span>
              <span className="herald-note" style={{ '--d': '2.9s' } as CSSProperties}>♬</span>
            </>
          )}
        </div>

        <div className="herald-scroll">
          <div className="herald-roller top" />
          <div className="herald-paper">
            <p className="herald-hear">Hear ye, hear ye!</p>
            <p className="herald-line l1">Be it known throughout the realm that the rock</p>
            <p className="herald-line herald-title l2">“{title}”</p>
            <p className="herald-line l3">is hereby and forever declared</p>
            <p className="herald-done">DONE!</p>
          </div>
          <div className="herald-roller bottom" />
        </div>
      </div>

      <span className="herald-hint">Click or press Esc to dismiss</span>
    </div>,
    document.body,
  );
}

const CONFETTI = ['#c8102e', '#f2c14e', '#1f6feb', '#2da44e', '#ffffff', '#8250df'];

/**
 * The herald himself, facing right toward his scroll. The arm and bugle are one
 * group so the whole thing swings up to his mouth around the shoulder; the legs
 * swing around the hip while he walks on.
 */
function HeraldSvg() {
  return (
    <svg className="herald-svg" viewBox="0 0 230 320" role="presentation">
      <g className="herald-body">
        {/* Legs — red and gold hose, the back one darker so they read apart. */}
        <g className="herald-leg back">
          <rect x="86" y="200" width="16" height="84" rx="6" fill="#8f0b21" />
          <rect x="86" y="236" width="16" height="10" fill="#c99a2e" />
          <ellipse cx="100" cy="288" rx="16" ry="7" fill="#2b1a10" />
        </g>
        <g className="herald-leg front">
          <rect x="104" y="200" width="16" height="84" rx="6" fill="#c8102e" />
          <rect x="104" y="236" width="16" height="10" fill="#f2c14e" />
          <ellipse cx="118" cy="288" rx="16" ry="7" fill="#3b2416" />
        </g>

        {/* Tabard, gold-trimmed, with a rock on the badge — he knows his brief. */}
        <path d="M68 108 L136 108 L148 214 L56 214 Z" fill="#c8102e" stroke="#f2c14e" strokeWidth="5" strokeLinejoin="round" />
        <path d="M102 108 L102 214" stroke="#f2c14e" strokeWidth="3" />
        <circle cx="102" cy="158" r="17" fill="#f2c14e" stroke="#8f0b21" strokeWidth="2" />
        <path d="M90 164 Q92 150 101 149 Q108 146 113 154 Q116 162 110 167 Q100 170 90 164 Z" fill="#7d7d7d" stroke="#4a4a4a" strokeWidth="1.5" />
        <ellipse cx="102" cy="108" rx="30" ry="9" fill="#fff8e7" stroke="#d9c79c" strokeWidth="2" />

        {/* Head, hat and feather. */}
        <circle cx="114" cy="80" r="21" fill="#f1c27d" />
        <circle cx="121" cy="73" r="2.6" fill="#2b1a10" />
        <path d="M113 66 Q119 62 126 66" stroke="#2b1a10" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle className="herald-cheek" cx="128" cy="86" r="8" fill="#f19c9c" />
        <path d="M100 40 Q60 10 48 46 Q70 30 98 58 Z" fill="#ffffff" stroke="#d0d0d0" strokeWidth="1.5" />
        <path d="M90 66 Q104 34 142 56 L140 64 Q114 54 94 72 Z" fill="#c8102e" stroke="#8f0b21" strokeWidth="2" />

        {/* Arm and bugle, swung up from the hip to the lips. */}
        <g className="herald-arm">
          <path d="M120 118 L140 96" stroke="#c8102e" strokeWidth="13" strokeLinecap="round" />
          <circle cx="141" cy="92" r="6.5" fill="#f1c27d" />
          <g className="herald-bugle">
            <rect x="132" y="84" width="72" height="6" rx="3" fill="#d4a017" stroke="#8a6508" strokeWidth="1" />
            <path d="M200 87 L226 70 L226 104 Z" fill="#e8b923" stroke="#8a6508" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M150 90 L196 90 L190 124 L173 112 L156 124 Z" fill="#c8102e" stroke="#f2c14e" strokeWidth="2.5" strokeLinejoin="round" />
            <circle cx="173" cy="104" r="5" fill="#f2c14e" />
          </g>
        </g>
      </g>
    </svg>
  );
}

/**
 * Ta-ta-ta-taaa, ta-taaaa: a bugle call on the C major triad, timed to start as
 * the bugle reaches his lips. Two detuned saw waves through a low-pass filter
 * sound brassy enough without being shrill, and the whole thing is kept quiet
 * — this plays in an office, mid-meeting.
 *
 * Returns the context so the caller can stop it, or null when the browser has
 * no Web Audio or will not start it; the celebration carries on silently.
 */
function playFanfare(): AudioContext | null {
  try {
    const ctx = new AudioContext();
    const master = ctx.createGain();
    master.gain.value = 0.07;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 2400;
    filter.connect(master).connect(ctx.destination);

    const notes: [number, number][] = [
      [392.0, 0.13],
      [523.25, 0.13],
      [659.25, 0.13],
      [783.99, 0.42],
      [659.25, 0.15],
      [783.99, 0.75],
    ];
    let t = ctx.currentTime + 1.65;
    for (const [freq, len] of notes) {
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(1, t + 0.025);
      env.gain.setValueAtTime(0.85, t + len * 0.6);
      env.gain.linearRampToValueAtTime(0, t + len);
      env.connect(filter);
      for (const detune of [-6, 6]) {
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.value = freq;
        osc.detune.value = detune;
        osc.connect(env);
        osc.start(t);
        osc.stop(t + len + 0.02);
      }
      t += len + 0.035;
    }
    return ctx;
  } catch {
    return null;
  }
}
